import React, { useRef, useEffect, useState } from 'react';

/**
 * Routes where the dense LED dot pattern competes with data-heavy UI
 * (configurator tabs, diagrams, admin tables). On these routes we render
 * a quieter, larger-spaced version of the background so that charts,
 * tables, and panel cards remain the visual focus.
 *
 * Per Usability Report finding U-01 (HIGH).
 */
const QUIET_ROUTE_PATTERNS: RegExp[] = [
  /^\/configurator(\/|$)/,
  /^\/configure(\/|$)/,
  /^\/admin(\/|$)/,
  /^\/visualize(\/|$)/,
  /^\/account(\/|$)/,
];

const isQuietRoute = (pathname: string): boolean =>
  QUIET_ROUTE_PATTERNS.some(re => re.test(pathname));

const LEDBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -1, y: -1, lastMove: 0 });
  const [pathname, setPathname] = useState<string>(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );

  // Track route changes (works with both push/replaceState from react-router and back/forward).
  useEffect(() => {
    const update = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', update);

    // Patch history methods so SPA navigations also trigger updates.
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      const ret = origPush.apply(this, args);
      window.dispatchEvent(new Event('lov-locationchange'));
      return ret;
    };
    window.history.replaceState = function (...args) {
      const ret = origReplace.apply(this, args);
      window.dispatchEvent(new Event('lov-locationchange'));
      return ret;
    };
    window.addEventListener('lov-locationchange', update);

    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener('lov-locationchange', update);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  const quiet = isQuietRoute(pathname);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Quiet mode: larger spacing, smaller dots, narrower glow → much less noise
    // on data-heavy screens while preserving the brand identity.
    const DOT_SPACING = quiet ? 28 : 16;
    const DOT_RADIUS = quiet ? 1.4 : 2.5;
    const GLOW_RADIUS = quiet ? 220 : 350;
    const BASE_OPACITY = quiet ? 0.07 : 0.18;
    const GLOW_BOOST = quiet ? 0.35 : 0.85;
    // How long after the last mouse movement we keep animating so the glow
    // can settle. Once the mouse is idle past this window we stop the RAF
    // chain entirely, allowing the browser to reach `document_idle`.
    const IDLE_MS = 400;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      scheduleDraw();
    };

    let pendingFrame = 0;

    const draw = () => {
      pendingFrame = 0;
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      for (let x = DOT_SPACING / 2; x < width; x += DOT_SPACING) {
        for (let y = DOT_SPACING / 2; y < height; y += DOT_SPACING) {
          // Gradient: blue on left, magenta on right
          const t = x / width;
          const r = Math.round(0 + t * 255);
          const g = Math.round(174 - t * 174);
          const b = 255;

          // Base opacity
          let opacity = BASE_OPACITY;

          // Mouse proximity boost
          if (mx >= 0) {
            const dx = x - mx;
            const dy = y - my;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < GLOW_RADIUS) {
              const factor = 1 - dist / GLOW_RADIUS;
              opacity += factor * factor * GLOW_BOOST;
            }
          }

          ctx.beginPath();
          ctx.arc(x, y, DOT_RADIUS, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
          ctx.fill();
        }
      }

      // Keep animating only while the mouse has been moving recently.
      // After IDLE_MS of stillness we stop scheduling new frames; the next
      // mousemove (or resize) will restart the chain.
      if (performance.now() - mouseRef.current.lastMove < IDLE_MS) {
        scheduleDraw();
      }
    };

    const scheduleDraw = () => {
      if (pendingFrame !== 0) return;
      pendingFrame = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);

    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      mouseRef.current.lastMove = performance.now();
      scheduleDraw();
    };
    window.addEventListener('mousemove', onMouseMove);

    const onMouseLeave = () => {
      mouseRef.current.x = -1;
      mouseRef.current.y = -1;
      mouseRef.current.lastMove = 0;
      scheduleDraw();
    };
    document.addEventListener('mouseleave', onMouseLeave);

    // Initial paint.
    scheduleDraw();

    return () => {
      if (pendingFrame !== 0) cancelAnimationFrame(pendingFrame);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [quiet]);


  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

export default LEDBackground;
